import { useState, useCallback } from 'react'
import { useLocation } from 'wouter'
import { ChevronLeft, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { usePaymentMethods } from '@/hooks/use-payment-methods'
import { useToast } from '@/hooks/use-toast'
import PaymentMethodsList from '@/components/PaymentMethodsList'
import DirectCardForm from '@/components/DirectCardForm'
import type { AddCardRequest } from '@/types/payment-methods'
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogCancel,
} from '@/components/ui/alert-dialog'

export default function PaymentMethodsPage() {
	const [, navigate] = useLocation()
	const { toast } = useToast()
	const { cards, isLoading, isCardExpired, deleteCard, addCard, updateCard } = usePaymentMethods()

	const [showModal, setShowModal] = useState(false)
	const [updateCardId, setUpdateCardId] = useState<number | null>(null)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const isUpdate = !!updateCardId

	const openAddCard = () => {
		setUpdateCardId(null)
		setShowModal(true)
	}

	const openUpdateCard = (cardId: number) => {
		setUpdateCardId(cardId)
		setShowModal(true)
	}

	const closeModal = () => {
		setShowModal(false)
		setUpdateCardId(null)
		setError(null)
		setIsSubmitting(false)
	}

	const handleFormSubmit = useCallback(async (data: AddCardRequest) => {
		setIsSubmitting(true)
		setError(null)

		try {
			if (isUpdate && updateCardId) {
				await updateCard(updateCardId, data)
				toast({
					title: 'Card Updated',
					description: `Card ending in ${data.ccNumber.slice(-4)} has been updated.`,
				})
			} else {
				await addCard(data)
				toast({
					title: 'Card Added',
					description: `Card ending in ${data.ccNumber.slice(-4)} has been saved.`,
				})
			}

			closeModal()
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to save card'
			setError(message)
			toast({ title: isUpdate ? 'Failed to Update Card' : 'Failed to Add Card', description: message, variant: 'destructive' })
		} finally {
			setIsSubmitting(false)
		}
	}, [isUpdate, updateCardId, updateCard, addCard, toast])

	const handleError = useCallback((message: string) => {
		setError(message)
	}, [])

	const handleSubmit = () => {
		setError(null)
		const form = document.querySelector<HTMLFormElement>('#payment-methods-card-form')
		form?.requestSubmit()
	}

	return (
		<div className="h-[100dvh] bg-[#18181B] text-white flex flex-col">
			<div className="max-w-5xl w-full mx-auto flex flex-col flex-1">
				{/* Header */}
				<div
					role="banner"
					className="sticky top-0 z-50 bg-[#18181B] flex items-center justify-start gap-2 text-white py-3 px-5"
				>
					<button
						type="button"
						aria-label="Go back"
						onClick={() => navigate('/account')}
						className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
					>
						<ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
					</button>
					<h1 className="text-[20px] leading-[18px] font-bold font-montserrat tracking-wide">
						Payment Methods
					</h1>
				</div>

				{/* Center content */}
				<div className="flex-1 flex items-center justify-center px-5 pb-20">
					<div className="w-full max-w-sm">
						<div className="bg-[#212124] rounded-3xl py-6 px-4">
							<PaymentMethodsList
								cards={cards}
								isLoading={isLoading}
								isCardExpired={isCardExpired}
								onAddCard={openAddCard}
								onUpdateCard={openUpdateCard}
								onDeleteCard={deleteCard}
							/>
						</div>
					</div>
				</div>
			</div>

			<AlertDialog open={showModal} onOpenChange={closeModal}>
				<AlertDialogContent className="rounded-2xl shadow-lg border fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 border-[#2F2F32] p-6 bg-[#18181B] w-[90%] sm:w-full max-w-sm mx-auto max-h-[85vh] overflow-y-auto">
					<AlertDialogHeader className="space-y-2 text-center sm:text-center">
						<AlertDialogTitle className="text-lg font-semibold text-white font-figtree text-center">
							{isUpdate ? 'Update Card' : 'Add Payment Card'}
						</AlertDialogTitle>
						<AlertDialogDescription className="text-sm text-[#BABABB] font-figtree text-center">
							{isUpdate
								? 'Enter your new card and billing details below.'
								: 'Enter your card and billing details below to add a payment method.'}
						</AlertDialogDescription>
					</AlertDialogHeader>

					<DirectCardForm
						formId="payment-methods-card-form"
						onSubmit={handleFormSubmit}
						onError={handleError}
						isSubmitting={isSubmitting}
						isUpdate={isUpdate}
					/>

					{error && (
						<p className="text-sm text-[#EF4444] font-figtree font-normal leading-[16px] text-center">
							{error}
						</p>
					)}

					<AlertDialogFooter className="flex flex-col gap-3 sm:flex-col sm:justify-center sm:space-x-0 items-center">
						<Button
							onClick={handleSubmit}
							disabled={isSubmitting}
							className="w-full h-[42px] bg-[#EF4444] font-figtree text-sm leading-[14px] font-semibold rounded-[26px] text-white shadow-[0_6px_20px_rgba(239,68,68,0.45)] active:scale-[0.96] hover:bg-[#dc2626]"
						>
							{isSubmitting ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									{isUpdate ? 'Updating...' : 'Adding...'}
								</>
							) : (
								isUpdate ? 'Update Card' : 'Add Card'
							)}
						</Button>
						<AlertDialogCancel
							onClick={closeModal}
							className="w-full h-[42px] !mt-0 text-sm leading-[14px] font-semibold font-figtree rounded-[26px] bg-white text-[#18181B] hover:bg-gray-200"
						>
							Cancel
						</AlertDialogCancel>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	)
}
